import { type LeadDetail, type MissingField } from '@utazzo/shared';

/** Query enquiry fields - optional for move-to-active (mirrors API helper). */
const OPTIONAL_ENQUIRY_ACTIVE_FIELDS = new Set([
  'queryTypeId',
  'querySourceId',
  'querySubSourceId',
  'queryMediumId',
  'querySourceUrl',
]);

/**
 * Client-side mirror of `apps/api/src/modules/leads/active-readiness.helper.ts`.
 * Used to pre-validate a Not Qualified → Active transition before the
 * server round-trip so the operator sees the "Required fields" modal
 * without an HTTP 400 first.
 *
 * The server is still the source of truth - a stale UI build won't
 * slip past because the same predicates run there too.
 */

interface MissingForActiveOptions {
  /**
   * When true, the six travel-intent predicates are also checked. Mirrors
   * the server helper (`missingForActive({ includeTravelIntent: true })`)
   * so a stale UI + a server that has enforcement OFF cannot drop the user
   * into a false-positive modal, and vice versa. Callers should read the
   * same enforcement flag from the client config feed.
   */
  includeTravelIntent?: boolean;
}

const REQUIRED: ReadonlyArray<{
  field: string;
  label: string;
  present: (l: LeadDetail) => boolean;
}> = [
  {
    field: 'customer.contact',
    label: 'Customer mobile or email',
    present: (l) =>
      Boolean((l.customer.mobileNumber ?? '').trim()) || Boolean((l.customer.email ?? '').trim()),
  },
  { field: 'leadCategory', label: 'Lead category', present: (l) => Boolean(l.leadCategory) },
  { field: 'priority', label: 'Lead Intent', present: (l) => Boolean(l.priority) },
  {
    field: 'expectedTravelStart',
    label: 'Travel start date',
    present: (l) => Boolean(l.expectedTravelStart),
  },
  {
    field: 'expectedTravelEnd',
    label: 'Travel end date',
    present: (l) => Boolean(l.expectedTravelEnd),
  },
  {
    field: 'travellerAdults',
    label: 'At least one adult traveller',
    present: (l) => typeof l.travellerAdults === 'number' && l.travellerAdults >= 1,
  },
  {
    field: 'budgetAmount',
    label: 'Budget',
    present: (l) => typeof l.budgetAmount === 'number' && l.budgetAmount > 0,
  },
  {
    field: 'departureCityId',
    label: 'Departure city',
    present: (l) => Boolean(l.departureCity?.id),
  },
  {
    field: 'destinationCityId',
    label: 'Destination city',
    present: (l) => Boolean(l.destinationCity?.id),
  },
];

/** Extended travel-intent set (2026-08-10). Mirror of the server-side list. */
const TRAVEL_INTENT: ReadonlyArray<{
  field: string;
  label: string;
  present: (l: LeadDetail) => boolean;
}> = [
  {
    field: 'isFirstTimeTraveler',
    label: 'First-time international traveller?',
    present: (l) => typeof l.isFirstTimeTraveler === 'boolean',
  },
  {
    field: 'hasValidPassport',
    label: 'Has valid passport?',
    present: (l) => typeof l.hasValidPassport === 'boolean',
  },
  {
    field: 'isFlightBooked',
    label: 'Flights already booked?',
    present: (l) => typeof l.isFlightBooked === 'boolean',
  },
  {
    field: 'bookingTimeline',
    label: 'Booking timeline',
    present: (l) => Boolean(l.bookingTimeline),
  },
  {
    field: 'isHoneymoonCouple',
    label: 'Honeymoon couple?',
    present: (l) => typeof l.isHoneymoonCouple === 'boolean',
  },
  {
    field: 'isDecisionMaker',
    label: 'Is contact the decision maker?',
    present: (l) => typeof l.isDecisionMaker === 'boolean',
  },
];

export const TRAVEL_INTENT_FIELDS = TRAVEL_INTENT.map((r) => r.field);

export function missingForActiveLead(
  lead: LeadDetail,
  opts: MissingForActiveOptions = {},
): MissingField[] {
  const base = REQUIRED.filter((r) => !r.present(lead)).map((r) => ({
    field: r.field,
    label: r.label,
  }));
  const intent = opts.includeTravelIntent
    ? TRAVEL_INTENT.filter((r) => !r.present(lead)).map((r) => ({
        field: r.field,
        label: r.label,
      }))
    : [];
  return [...base, ...intent].filter((m) => !OPTIONAL_ENQUIRY_ACTIVE_FIELDS.has(m.field));
}

/**
 * Backlog gate. Returns just the travel-intent gaps for an already-active
 * lead. Client-side pre-check for follow-up + publish flows so the seller
 * sees the modal without an HTTP 400 first. Server backstops with the same
 * predicates via collectTravelIntentGaps.
 */
export function missingTravelIntentForBacklog(lead: LeadDetail): MissingField[] {
  return TRAVEL_INTENT.filter((r) => !r.present(lead)).map((r) => ({
    field: r.field,
    label: r.label,
  }));
}
